import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    Activity,
    AlertTriangle,
    Brain,
    CalendarRange,
    ChevronDown,
    Database,
    Newspaper,
    ShieldCheck,
    X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/Button';
import { useMobile } from '@/hooks/useResponsiveDesign';
import { useTranslation } from '@/hooks/useTranslation';
import { API_BASE_URL } from '@/config';
import { authenticatedFetch, isAuthExpired, isAuthExpiredError } from '@/utils/authFetch';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

type SignalStatusPayload = {
    system_status?: string;
    latest_broadcast?: any;
    strategy_signals?: {
        forecast?: any;
    };
    strategy_state?: {
        forecast?: 'fresh' | 'stale' | 'missing' | string;
    };
    signals_by_symbol?: Record<string, any> | null;
    signal_state_by_symbol?: Record<string, string> | null;
    message?: string | null;
};

type MarketNewsPayload = {
    generated_at?: string;
    candle_open_at?: string;
    candle_close_at?: string;
    next_refresh_at?: string;
    asset_news?: Array<Record<string, any>>;
    macro_news?: Array<Record<string, any>>;
} | null;

type ForecastPayload = Record<string, any> | null;

type TriggeringNewsItem = {
    title?: string;
    source?: string;
    published_at?: string;
    summary?: string;
    url?: string;
    relevance_reason?: string;
    bucket?: string;
};

type ConfirmationDetail = {
    confirmed_at?: string;
    valid_from?: string;
    valid_until?: string;
    summary?: string;
    primary_reason?: string;
    confidence_pct?: number;
    confidence_level?: 'high' | 'medium' | 'low' | string;
    confirmation_reasons?: string[];
    triggering_news?: TriggeringNewsItem[];
    confidence_factors?: {
        forecast_confidence_pct?: number;
        llm_confidence_pct?: number;
        blended_confidence_pct?: number;
        supporting_factors?: string[];
        warnings?: string[];
    };
    forecast_impact?: {
        signal?: string;
        timeframe?: string;
        expected_move_pct?: number;
        current_price?: number;
        forecast_price?: number;
        model_weights?: Record<string, number> | null;
        bounds_metadata?: Record<string, number | string> | null;
        narrative?: string;
    };
    recommended_actions?: string[];
    raw_debug?: Record<string, any>;
};

type FallbackLlmReasoning = {
    summary?: string;
    confidence_pct?: number;
    reasons?: string[];
    warnings?: string[];
    recommended_actions?: string[];
    generated_at?: string;
    valid_until?: string;
    source?: string;
};

type NormalizedSignalStatus = {
    signal: string;
    confidence: number | null;
    generated_at: string;
    strategy: string;
    reason: string;
    symbol?: string;
    source_strategy_key?: string;
    confirmation?: string;
    llm_confidence?: number;
    llm_risk_level?: string;
    llm_reasons?: string[];
    llm_warnings?: string[];
    potential_up_move?: number;
    potential_down_move?: number;
    confirmation_detail?: ConfirmationDetail | null;
    fallback_llm_reasoning?: FallbackLlmReasoning | null;
};

const CANONICAL_SIGNAL_LABELS = new Set(['BUY', 'SELL', 'HOLD', 'LONG', 'SHORT']);
const IS_DEV_ENV = process.env.NODE_ENV !== 'production';

const sanitizeSignalReason = (raw?: string | null): string => {
    const text = String(raw || '')
        .replace(/^Signal Reason:\s*/i, '')
        .trim();
    if (!text) return '';
    if (
        /rollback\(\)|invalid transaction|sqlalche\.me|can't reconnect until invalid transaction/i.test(
            text,
        )
    ) {
        return 'AI reasoning temporarily unavailable.';
    }

    const cleaned = text
        .replace(/^<+/, '')
        .replace(/>+$/, '')
        .replace(/^Signal Reason:\s*/i, '')
        .replace(/\(\{\s*,?\s*"confidence"\s*:\s*[0-9.]+.*$/i, '')
        .replace(/\{,\s*"confidence"\s*:\s*[0-9.]+.*$/i, '')
        .replace(/\s+/g, ' ')
        .trim();

    return cleaned.length > 280 ? `${cleaned.slice(0, 277)}...` : cleaned;
};

const sanitizeDisplayText = (raw?: string | null, maxLength = 280): string => {
    const text = sanitizeSignalReason(raw)
        .replace(/^```(?:json)?/i, '')
        .replace(/```$/i, '')
        .trim();

    if (!text) return '';
    if (/^[\[{(,:-]+$/.test(text)) return '';
    if (
        /^"?(confidence|decision|risk_level|market_regime|primary_news_bucket|primary_news_index)"?\s*:/i.test(
            text,
        )
    )
        return '';
    if (
        (text.startsWith('{') && text.endsWith('}')) ||
        (text.startsWith('[') && text.endsWith(']'))
    )
        return '';

    const propertyMatch = text.match(
        /^"?(summary|reason|message|title|headline|warning|action)"?\s*:\s*"(.+)"[,]?$/i,
    );
    const normalized = (propertyMatch ? propertyMatch[2] : text).trim().replace(/^[-*]\s*/, '');
    if (!normalized) return '';

    return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 3)}...` : normalized;
};

const sanitizeDisplayList = (
    values?: Array<string | null | undefined>,
    maxLength = 220,
): string[] => {
    if (!Array.isArray(values)) return [];
    return Array.from(
        new Set(values.map(value => sanitizeDisplayText(value, maxLength)).filter(Boolean)),
    );
};

const resolveSignalEnvelope = (broadcast?: any) => {
    if (!broadcast || typeof broadcast !== 'object') {
        return { nested: null, timestamp: undefined as string | undefined };
    }

    const root = broadcast?.data ?? broadcast;
    const signalContainer = root?.signal && typeof root.signal === 'object' ? root.signal : root;
    const nested =
        signalContainer?.signal && typeof signalContainer.signal === 'object'
            ? signalContainer.signal
            : signalContainer;
    const timestamp =
        broadcast?.timestamp ||
        root?.timestamp ||
        signalContainer?.generated_at ||
        signalContainer?.timestamp ||
        nested?.generated_at;

    return {
        nested: nested && typeof nested === 'object' ? nested : null,
        timestamp,
    };
};

const normalizeSignalValue = (raw?: string | null): string => {
    const normalized = String(raw || 'HOLD').trim();
    if (!normalized) return 'HOLD';

    const canonical = normalized.toUpperCase();
    if (CANONICAL_SIGNAL_LABELS.has(canonical)) {
        return canonical;
    }

    return normalized;
};

const normalizeSignalEnvelope = (broadcast?: any): NormalizedSignalStatus | null => {
    const { nested, timestamp } = resolveSignalEnvelope(broadcast);
    if (!nested || (!nested.signal && !nested.direction && !timestamp)) {
        return null;
    }

    const root = broadcast?.data ?? broadcast;
    const symbolSource = root?.symbol ?? nested?.symbol;

    return {
        signal: normalizeSignalValue(nested?.signal || nested?.direction || 'HOLD'),
        confidence:
            typeof nested?.confidence === 'number' && Number.isFinite(nested.confidence)
                ? nested.confidence
                : null,
        generated_at: timestamp || new Date().toISOString(),
        strategy: nested?.strategy || nested?.strategy_name || 'Forecast + EMA-20',
        reason: sanitizeSignalReason(nested?.reason || ''),
        symbol:
            typeof symbolSource === 'string' && symbolSource
                ? symbolSource.toUpperCase()
                : undefined,
        source_strategy_key: broadcast?.strategy_key,
        confirmation: nested?.confirmation,
        llm_confidence:
            typeof nested?.llm_confidence === 'number' ? nested.llm_confidence : undefined,
        llm_risk_level: nested?.llm_risk_level,
        llm_reasons: Array.isArray(nested?.llm_reasons) ? nested.llm_reasons : [],
        llm_warnings: Array.isArray(nested?.llm_warnings) ? nested.llm_warnings : [],
        potential_up_move:
            typeof nested?.potential_up_move === 'number' ? nested.potential_up_move : undefined,
        potential_down_move:
            typeof nested?.potential_down_move === 'number'
                ? nested.potential_down_move
                : undefined,
        confirmation_detail: nested?.confirmation_detail || null,
        fallback_llm_reasoning: nested?.fallback_llm_reasoning || null,
    };
};

const normalizeSignalPayload = (
    payload?: SignalStatusPayload | null,
): NormalizedSignalStatus | null => {
    if (!payload) return null;

    const forecastEnvelope = payload?.strategy_signals?.forecast;
    return (
        normalizeSignalEnvelope(forecastEnvelope) ||
        normalizeSignalEnvelope(payload?.latest_broadcast) ||
        normalizeSignalEnvelope(payload)
    );
};

const getSignalTimestamp = (status?: NormalizedSignalStatus | null): number => {
    if (!status?.generated_at) {
        return 0;
    }

    const parsed = new Date(status.generated_at).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
};

const preferNewerSignalStatus = (
    current: NormalizedSignalStatus | null,
    incoming: NormalizedSignalStatus | null,
): NormalizedSignalStatus | null => {
    if (!incoming) {
        return current;
    }

    if (!current) {
        return incoming;
    }

    return getSignalTimestamp(incoming) >= getSignalTimestamp(current) ? incoming : current;
};

interface TradingSignalStatusCardProps {
    initialData?: SignalStatusPayload | null;
    marketNews?: MarketNewsPayload;
    forecast?: ForecastPayload;
}

const formatDateTime = (value?: string | null): string => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const normalizeConfidencePercent = (raw?: number | null): number | null => {
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
    const numeric = raw;
    const value = numeric <= 1 ? numeric * 100 : numeric;
    if (!Number.isFinite(value)) return null;
    return Math.max(0, Math.min(100, value));
};

const getConfidenceTone = (confidencePercent: number | null) => {
    if (confidencePercent === null) {
        return {
            label: 'ML confidence unavailable',
            badge: 'text-slate-700 bg-slate-100 border border-slate-200 dark:text-slate-300 dark:bg-slate-900/50 dark:border-slate-700',
            panel: 'border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-900/30',
        };
    }
    if (confidencePercent >= 70) {
        return {
            label: 'High Confidence',
            badge: 'text-emerald-800 bg-gradient-to-r from-emerald-100 to-green-200 border border-emerald-200 dark:text-emerald-200 dark:from-emerald-950/60 dark:to-green-900/40 dark:border-emerald-900/60',
            panel: 'border-emerald-200 bg-emerald-50/80 dark:border-emerald-900/60 dark:bg-emerald-950/30',
        };
    }
    if (confidencePercent >= 40) {
        return {
            label: 'Medium Confidence',
            badge: 'text-amber-900 bg-gradient-to-r from-amber-100 to-yellow-200 border border-amber-200 dark:text-amber-200 dark:from-amber-950/50 dark:to-yellow-900/30 dark:border-amber-900/60',
            panel: 'border-amber-200 bg-amber-50/80 dark:border-amber-900/60 dark:bg-amber-950/30',
        };
    }
    return {
        label: 'Low Confidence',
        badge: 'text-red-800 bg-gradient-to-r from-red-100 to-rose-200 border border-red-200 dark:text-red-200 dark:from-red-950/60 dark:to-rose-900/30 dark:border-red-900/60',
        panel: 'border-red-200 bg-red-50/80 dark:border-red-900/60 dark:bg-red-950/30',
    };
};

export default function TradingSignalStatusCard({
    initialData = null,
    marketNews = null,
    forecast = null,
}: TradingSignalStatusCardProps) {
    const { isMobile } = useMobile();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(!initialData);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(initialData?.message ?? null);
    const [status, setStatus] = useState<NormalizedSignalStatus | null>(() =>
        normalizeSignalPayload(initialData),
    );
    const [systemStatus, setSystemStatus] = useState<string>(
        initialData?.system_status || (status ? 'active' : 'no_signal'),
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Full status payload (holds signals_by_symbol for the pair tiles)
    const [rawPayload, setRawPayload] = useState<SignalStatusPayload | null>(initialData);
    // Pair tile the user focused for the detail view (null = newest overall)
    const [selectedPair, setSelectedPair] = useState<string | null>(null);
    const statusRef = useRef<NormalizedSignalStatus | null>(status);

    useEffect(() => {
        statusRef.current = status;
    }, [status]);

    const fetchStatus = useCallback(async () => {
        if (isAuthExpired()) {
            setLoading(false);
            setError(null);
            setSystemStatus('degraded');
            setNotice('Session expired. Sign in again to continue.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const latestRes = await authenticatedFetch(
                `${API_BASE_URL}/api/shared-signals/signals/status`,
                {
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );

            let latestData: SignalStatusPayload = {};
            if (latestRes.ok) {
                const contentType = latestRes.headers.get('content-type') || '';
                if (contentType.includes('application/json')) {
                    latestData = await latestRes.json();
                } else {
                    if (IS_DEV_ENV) {
                        const textResponse = await latestRes.text();
                        console.error(
                            '[TradingSignalStatusCard] Expected JSON but got:',
                            contentType,
                            textResponse.substring(0, 200),
                        );
                    }
                    throw new Error('Signal status returned a non-JSON response');
                }
            } else {
                const fetchError: Error & { status?: number } = new Error(
                    `Signals API failed: ${latestRes.status} ${latestRes.statusText}`,
                );
                fetchError.status = latestRes.status;
                if (IS_DEV_ENV) {
                    const textResponse = await latestRes.text();
                    console.error('[TradingSignalStatusCard] Signals API error:', {
                        status: latestRes.status,
                        statusText: latestRes.statusText,
                        body: textResponse.substring(0, 200),
                    });
                }
                throw fetchError;
            }

            const normalized = normalizeSignalPayload(latestData);
            setStatus(current => preferNewerSignalStatus(current, normalized));
            setRawPayload(latestData);

            const resolvedStatus = preferNewerSignalStatus(statusRef.current, normalized);
            setSystemStatus(latestData.system_status || (resolvedStatus ? 'active' : 'no_signal'));
            setNotice(latestData.message || (resolvedStatus ? null : 'No active signal yet'));
        } catch (e: any) {
            if (isAuthExpiredError(e)) {
                setSystemStatus('degraded');
                setNotice('Session expired. Sign in again to continue.');
                setError(null);
                return;
            }

            if (IS_DEV_ENV) {
                console.error('[TradingSignalStatusCard] Error:', e);
            }
            setSystemStatus(prev => (prev === 'active' ? 'degraded' : prev || 'degraded'));
            setNotice(
                statusRef.current
                    ? 'Signal status is temporarily unavailable. Showing the last successful update.'
                    : 'Signal status is temporarily unavailable.',
            );
            if (!statusRef.current) {
                setError(e?.message || t('tradingSignalStatus.errorLoading'));
            }
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (initialData) {
            const normalized = normalizeSignalPayload(initialData);
            const resolvedStatus = preferNewerSignalStatus(statusRef.current, normalized);
            setStatus(current => preferNewerSignalStatus(current, normalized));
            setRawPayload(initialData);
            setSystemStatus(initialData.system_status || (resolvedStatus ? 'active' : 'no_signal'));
            setNotice(initialData.message || (resolvedStatus ? null : 'No active signal yet'));
            setLoading(false);
        }
    }, [initialData]);

    useEffect(() => {
        void fetchStatus();
        const id = setInterval(() => {
            if (!isAuthExpired()) {
                void fetchStatus();
            }
        }, 30_000);
        return () => clearInterval(id);
    }, [fetchStatus]);

    // Per-pair matrix tiles: newest forecast signal per configured pair.
    const pairTiles = useMemo(() => {
        const bySymbol = rawPayload?.signals_by_symbol;
        if (!bySymbol || typeof bySymbol !== 'object') return null;
        const states = rawPayload?.signal_state_by_symbol || {};
        return Object.keys(bySymbol).map(symbol => {
            const envelope = bySymbol[symbol];
            return {
                symbol,
                status: envelope ? normalizeSignalEnvelope(envelope) : null,
                state: String(states[symbol] || 'missing'),
            };
        });
    }, [rawPayload]);

    // The detail view shows the selected pair's signal when the user clicked
    // a tile; otherwise it keeps the legacy newest-signal behavior.
    const activeStatus = useMemo(() => {
        if (selectedPair && pairTiles) {
            const tile = pairTiles.find(
                entry => entry.symbol.toUpperCase() === selectedPair.toUpperCase(),
            );
            if (tile?.status) return tile.status;
        }
        return status;
    }, [selectedPair, pairTiles, status]);

    const confidencePercent = useMemo(
        () => normalizeConfidencePercent(activeStatus?.confidence),
        [activeStatus],
    );
    const tone = useMemo(() => getConfidenceTone(confidencePercent), [confidencePercent]);

    const color =
        !activeStatus || activeStatus.signal === 'HOLD'
            ? 'text-gray-600 dark:text-gray-400'
            : activeStatus.signal === 'BUY'
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400';

    const confirmationDetail = activeStatus?.confirmation_detail;
    const fallbackReasoning = activeStatus?.fallback_llm_reasoning;
    const sanitizedSummary = sanitizeDisplayText(
        confirmationDetail?.summary ||
            confirmationDetail?.primary_reason ||
            activeStatus?.reason ||
            activeStatus?.llm_reasons?.[0],
    );
    const sanitizedFallbackSummary = sanitizeDisplayText(fallbackReasoning?.summary);
    const sanitizedFallbackReasons = sanitizeDisplayList(fallbackReasoning?.reasons).filter(
        reason => reason !== sanitizedFallbackSummary,
    );
    const sanitizedFallbackWarnings = sanitizeDisplayList(fallbackReasoning?.warnings);
    const sanitizedPrimaryReason = sanitizeDisplayText(
        confirmationDetail?.primary_reason || confirmationDetail?.summary,
    );
    const sanitizedConfirmationReasons = sanitizeDisplayList(
        confirmationDetail?.confirmation_reasons,
    );
    const sanitizedSupportingFactors = sanitizeDisplayList(
        confirmationDetail?.confidence_factors?.supporting_factors,
    );
    const sanitizedConfidenceWarnings = sanitizeDisplayList(
        confirmationDetail?.confidence_factors?.warnings,
    );
    const sanitizedRecommendedActions = sanitizeDisplayList(
        confirmationDetail?.recommended_actions,
    );
    const normalizedSignal = normalizeSignalValue(activeStatus?.signal || 'HOLD');
    const translatedSignal = activeStatus
        ? t(`tradingSignalStatus.signals.${normalizedSignal}`)
        : '';
    const signalLabel =
        loading && !activeStatus
            ? '--'
            : activeStatus
              ? !translatedSignal ||
                translatedSignal === `tradingSignalStatus.signals.${normalizedSignal}`
                  ? normalizedSignal
                  : translatedSignal
              : 'No signal yet';

    const summaryText = sanitizedSummary || sanitizedFallbackSummary;
    const triggeringNews = confirmationDetail?.triggering_news || [];
    const forecastImpact = confirmationDetail?.forecast_impact || {};
    const modelWeights = (forecastImpact?.model_weights || forecast?.model_weights || {}) as Record<
        string,
        number
    >;
    const boundsMetadata = (forecastImpact?.bounds_metadata ||
        forecast?.bounds_metadata ||
        {}) as Record<string, number | string>;
    const validityStart =
        confirmationDetail?.valid_from ||
        confirmationDetail?.confirmed_at ||
        activeStatus?.generated_at;
    const validityEnd =
        confirmationDetail?.valid_until ||
        marketNews?.next_refresh_at ||
        activeStatus?.generated_at;
    const staleFallbackBanner = null;
    return (
        <>
            <Card className={`${isMobile ? 'mb-4 w-full' : 'mb-6'}`}>
                <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
                    <div className="flex items-start gap-3">
                        <Activity
                            className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} mt-0.5 ${color}`}
                        />
                        <div className="space-y-1">
                            <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold`}>
                                {t('tradingSignalStatus.title')}
                            </h3>
                            <p className="text-xs text-muted-foreground">
                                {activeStatus
                                    ? activeStatus.strategy
                                    : t('tradingSignalStatus.description')}
                            </p>
                            {/* Legacy inline summary: only when the payload has no
                                per-pair tiles. With tiles present, each pair
                                carries its own badge + time inside its tile. */}
                            {(!pairTiles || pairTiles.length === 0) && (
                                <div className="flex flex-wrap items-center gap-2 pt-0.5">
                                    <span className="text-xs text-muted-foreground">
                                        {t('tradingSignalStatus.signalLabel')}
                                    </span>
                                    <span className={`text-xs font-semibold ${color}`}>
                                        {signalLabel}
                                    </span>
                                    {activeStatus?.symbol && (
                                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold tracking-wide text-foreground/80">
                                            {activeStatus.symbol}
                                        </span>
                                    )}
                                    <span
                                        className={cn(
                                            'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
                                            tone.badge,
                                        )}
                                    >
                                        {confidencePercent === null
                                            ? tone.label
                                            : `${formatNumber(confidencePercent, confidencePercent % 1 === 0 ? 0 : 1)}% ${tone.label}`}
                                    </span>
                                    <span className="text-[10px] text-muted-foreground">
                                        {t('tradingSignalStatus.lastUpdateLabel')}{' '}
                                        {loading && !activeStatus
                                            ? '--'
                                            : formatDateTime(activeStatus?.generated_at)}
                                    </span>
                                </div>
                            )}
                            {summaryText && (
                                <p className="max-w-2xl text-sm leading-6 text-foreground/90">
                                    {summaryText}
                                </p>
                            )}
                            {staleFallbackBanner && (
                                <p className="max-w-2xl text-xs font-medium text-amber-700 dark:text-amber-300">
                                    {staleFallbackBanner}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Per-pair signal tiles: one per configured pair from the
                        backend matrix. Click a tile to focus the detail view. */}
                    {pairTiles && pairTiles.length > 0 && (
                        <div
                            className={`mt-4 grid ${isMobile ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-4'} gap-2`}
                            data-testid="pair-signal-tiles"
                        >
                            {pairTiles.map(tile => {
                                const tileSignal = tile.status
                                    ? normalizeSignalValue(tile.status.signal)
                                    : null;
                                const tileSignalClass =
                                    tileSignal === 'BUY'
                                        ? 'text-green-600 dark:text-green-400'
                                        : tileSignal === 'SELL'
                                          ? 'text-red-600 dark:text-red-400'
                                          : 'text-gray-600 dark:text-gray-400';
                                const tileConfidence = normalizeConfidencePercent(
                                    tile.status?.confidence,
                                );
                                const tileTone = getConfidenceTone(tileConfidence);
                                const tileUpdated = tile.status?.generated_at
                                    ? formatDateTime(tile.status.generated_at)
                                    : '--';
                                const isSelected =
                                    !!selectedPair &&
                                    tile.symbol.toUpperCase() === selectedPair.toUpperCase();
                                return (
                                    <button
                                        key={tile.symbol}
                                        type="button"
                                        onClick={() =>
                                            setSelectedPair(isSelected ? null : tile.symbol)
                                        }
                                        aria-pressed={isSelected}
                                        className={cn(
                                            'rounded-lg border px-2.5 py-2 text-left transition-colors',
                                            isSelected
                                                ? 'border-primary bg-primary/10'
                                                : 'border-border/60 hover:bg-muted/50',
                                            tile.status ? '' : 'border-dashed opacity-70',
                                        )}
                                        data-testid={`pair-tile-${tile.symbol}`}
                                        data-selected={isSelected ? 'true' : 'false'}
                                    >
                                        <div className="flex items-center justify-between gap-1">
                                            <span className="text-[11px] font-bold tracking-wide text-foreground/80">
                                                {tile.symbol}
                                            </span>
                                            {tile.state === 'stale' && (
                                                <span className="text-[9px] font-medium text-amber-600 dark:text-amber-400">
                                                    ●
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-0.5 flex items-baseline justify-between gap-1">
                                            <span
                                                className={`text-sm font-bold ${tileSignalClass}`}
                                            >
                                                {tileSignal ?? '--'}
                                            </span>
                                            <span
                                                className={cn(
                                                    'inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold leading-none',
                                                    tileTone.badge,
                                                )}
                                                data-testid={`pair-tile-badge-${tile.symbol}`}
                                            >
                                                {tileConfidence === null
                                                    ? tileTone.label
                                                    : `${formatNumber(tileConfidence, tileConfidence % 1 === 0 ? 0 : 1)}% ${tileTone.label}`}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-[9px] text-muted-foreground">
                                            {tileUpdated}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {!loading && confirmationDetail && (
                        <button
                            type="button"
                            className={cn(
                                'mt-4 w-full rounded-2xl border p-4 text-left transition hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-ring',
                                tone.panel,
                            )}
                            onClick={() => setIsModalOpen(true)}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <ShieldCheck className="h-4 w-4" />
                                        <span className="text-sm font-semibold">
                                            Confirmed Signal
                                        </span>
                                    </div>
                                    {sanitizedPrimaryReason && (
                                        <p className="text-sm leading-6 text-foreground/90">
                                            {sanitizedPrimaryReason}
                                        </p>
                                    )}
                                    {triggeringNews[0]?.title && (
                                        <p className="text-xs text-muted-foreground">
                                            Lead headline: {triggeringNews[0].title}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <CalendarRange className="h-3.5 w-3.5" />
                                        <span className="font-semibold text-foreground">
                                            {formatDateTime(validityStart)} to{' '}
                                            {formatDateTime(validityEnd)}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                                    <span>View details</span>
                                    <ChevronDown className="h-4 w-4" />
                                </div>
                            </div>
                        </button>
                    )}

                    {!loading &&
                        !confirmationDetail &&
                        (status?.reason ||
                            typeof status?.potential_up_move === 'number' ||
                            typeof status?.potential_down_move === 'number') && (
                            <div className="mt-3 flex flex-col gap-2 max-w-xl">
                                {fallbackReasoning &&
                                    status?.signal === '__removed_neural_reasoning__' && (
                                        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-3 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
                                            <div className="font-semibold">AI chart reasoning</div>
                                            {sanitizedFallbackSummary && (
                                                <div className="mt-1">
                                                    {sanitizedFallbackSummary}
                                                    {typeof fallbackReasoning.confidence_pct ===
                                                    'number'
                                                        ? ` (${formatNumber(fallbackReasoning.confidence_pct, fallbackReasoning.confidence_pct % 1 === 0 ? 0 : 1)}%)`
                                                        : ''}
                                                </div>
                                            )}
                                            {sanitizedFallbackReasons.length > 0 && (
                                                <ul className="mt-2 space-y-1">
                                                    {sanitizedFallbackReasons.map(
                                                        (reason, index) => (
                                                            <li key={`${reason}-${index}`}>
                                                                - {reason}
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            )}
                                            {sanitizedFallbackWarnings.length > 0 && (
                                                <ul className="mt-2 space-y-1 text-amber-800 dark:text-amber-200">
                                                    {sanitizedFallbackWarnings.map(
                                                        (warning, index) => (
                                                            <li key={`${warning}-${index}`}>
                                                                - {warning}
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            )}
                                            {!sanitizedFallbackSummary &&
                                                sanitizedFallbackReasons.length === 0 &&
                                                sanitizedFallbackWarnings.length === 0 && (
                                                    <div className="mt-1">
                                                        AI chart analysis reviewed the latest ML
                                                        forecast.
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                {status?.reason && (
                                    <div className="text-xs text-muted-foreground bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-xl">
                                        <strong className="text-xs">Signal Reason:</strong>{' '}
                                        {status.reason}
                                    </div>
                                )}
                                {(typeof status?.potential_up_move === 'number' ||
                                    typeof status?.potential_down_move === 'number') && (
                                    <div className="text-xs text-muted-foreground bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-xl">
                                        <strong className="text-xs">Potential Move:</strong> Up:{' '}
                                        {formatNumber(status?.potential_up_move ?? 0, 4)}% | Down:{' '}
                                        {formatNumber(status?.potential_down_move ?? 0, 4)}%
                                    </div>
                                )}
                            </div>
                        )}

                    {!loading && notice && (
                        <div className="mt-3 text-xs text-muted-foreground">
                            {systemStatus === 'degraded' ? `Unavailable: ${notice}` : notice}
                        </div>
                    )}

                    {error && !status && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                            <AlertTriangle className="w-4 h-4" />
                            <span>{error}</span>
                        </div>
                    )}
                </CardContent>
            </Card>

            {isModalOpen && confirmationDetail && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
                    <div className="relative max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-3xl border border-border bg-background shadow-2xl">
                        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
                            <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-3">
                                    <h3 className="text-xl font-semibold">
                                        Confirmed Signal Details
                                    </h3>
                                    <span
                                        className={cn(
                                            'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold',
                                            tone.badge,
                                        )}
                                    >
                                        {confidencePercent === null
                                            ? 'ML confidence unavailable'
                                            : `${formatNumber(confidencePercent, confidencePercent % 1 === 0 ? 0 : 1)}% confidence`}
                                    </span>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    Confirmed{' '}
                                    {formatDateTime(
                                        confirmationDetail.confirmed_at || status?.generated_at,
                                    )}
                                    . Valid through {formatDateTime(validityEnd)}.
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setIsModalOpen(false)}
                            >
                                <X className="mr-1 h-4 w-4" />
                                Close
                            </Button>
                        </div>

                        <div className="max-h-[calc(90vh-88px)] overflow-y-auto px-6 py-5">
                            <div className="grid gap-4 lg:grid-cols-2">
                                <section className="rounded-2xl border border-border/70 bg-card p-4">
                                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                        <ShieldCheck className="h-4 w-4" />
                                        Confirmation Reason
                                    </div>
                                    <ul className="space-y-2 text-sm leading-6 text-foreground/90">
                                        {(sanitizedPrimaryReason
                                            ? [sanitizedPrimaryReason]
                                            : []
                                        ).map(reason => (
                                            <li key={reason}>{reason}</li>
                                        ))}
                                        {sanitizedConfirmationReasons
                                            .filter(reason => reason !== sanitizedPrimaryReason)
                                            .map((reason, index) => (
                                                <li key={`${reason}-${index}`}>- {reason}</li>
                                            ))}
                                    </ul>
                                </section>

                                <section className="rounded-2xl border border-border/70 bg-card p-4">
                                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                        <Newspaper className="h-4 w-4" />
                                        Triggering News
                                    </div>
                                    <div className="space-y-3 text-sm">
                                        {triggeringNews.length ? (
                                            triggeringNews.map((item, index) => (
                                                <div
                                                    key={`${item.title || 'news'}-${index}`}
                                                    className="rounded-xl border border-border/60 bg-muted/30 p-3"
                                                >
                                                    <div className="font-medium">
                                                        {item.title || 'Untitled headline'}
                                                    </div>
                                                    <div className="mt-1 text-xs text-muted-foreground">
                                                        {item.source || 'Unknown source'} |{' '}
                                                        {formatDateTime(item.published_at)}
                                                    </div>
                                                    {sanitizeDisplayText(item.relevance_reason) && (
                                                        <div className="mt-2 text-sm text-foreground/90">
                                                            {sanitizeDisplayText(
                                                                item.relevance_reason,
                                                            )}
                                                        </div>
                                                    )}
                                                    {item.url && (
                                                        <a
                                                            className="mt-2 inline-block text-xs font-medium text-primary underline"
                                                            href={item.url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Open source
                                                        </a>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="rounded-xl border border-dashed border-border/70 p-3 text-sm text-muted-foreground">
                                                No specific triggering headlines were returned for
                                                this confirmation.
                                            </div>
                                        )}
                                    </div>
                                </section>

                                <section className="rounded-2xl border border-border/70 bg-card p-4">
                                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                        <Brain className="h-4 w-4" />
                                        Confidence Factors
                                    </div>
                                    <ul className="space-y-2 text-sm leading-6">
                                        <li>
                                            ML forecast confidence:{' '}
                                            {confidencePercent === null
                                                ? 'unavailable'
                                                : `${formatNumber(confidencePercent, 1)}%`}
                                        </li>
                                        {sanitizedSupportingFactors.map((factor, index) => (
                                            <li key={`${factor}-${index}`}>- {factor}</li>
                                        ))}
                                        {sanitizedConfidenceWarnings.map((warning, index) => (
                                            <li
                                                key={`${warning}-${index}`}
                                                className="text-amber-700 dark:text-amber-300"
                                            >
                                                - {warning}
                                            </li>
                                        ))}
                                    </ul>
                                </section>

                                <section className="rounded-2xl border border-border/70 bg-card p-4">
                                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                        <Activity className="h-4 w-4" />
                                        Forecast Impact
                                    </div>
                                    <ul className="space-y-2 text-sm leading-6">
                                        <li>
                                            Direction:{' '}
                                            {forecastImpact.signal || status?.signal || 'HOLD'} on{' '}
                                            {forecastImpact.timeframe ||
                                                forecast?.timeframe ||
                                                '1h'}
                                        </li>
                                        <li>
                                            Expected move:{' '}
                                            {formatNumber(
                                                forecastImpact.expected_move_pct ??
                                                    (Math.abs(
                                                        Number(forecast?.highest_price ?? 0) -
                                                            Number(forecast?.current_price ?? 0),
                                                    ) /
                                                        Math.max(
                                                            Number(forecast?.current_price ?? 1),
                                                            1,
                                                        )) *
                                                        100,
                                                2,
                                            )}
                                            %
                                        </li>
                                        <li>
                                            Current price:{' '}
                                            {formatCurrency(
                                                Number(
                                                    forecastImpact.current_price ??
                                                        forecast?.current_price ??
                                                        0,
                                                ),
                                            )}
                                        </li>
                                        <li>
                                            Forecast target:{' '}
                                            {formatCurrency(
                                                Number(
                                                    forecastImpact.forecast_price ??
                                                        forecast?.highest_price ??
                                                        forecast?.forecast_price ??
                                                        0,
                                                ),
                                            )}
                                        </li>
                                        <li>
                                            Market regime:{' '}
                                            {status?.llm_risk_level || 'Not provided'}
                                        </li>
                                        <li>
                                            {forecastImpact.narrative ||
                                                'The news-confirmed signal remains aligned with the active forecast window.'}
                                        </li>
                                    </ul>
                                </section>

                                <section className="rounded-2xl border border-border/70 bg-card p-4 lg:col-span-2">
                                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                                        <ShieldCheck className="h-4 w-4" />
                                        Recommended Actions
                                    </div>
                                    <ul className="grid gap-2 md:grid-cols-2">
                                        {sanitizedRecommendedActions.map((action, index) => (
                                            <li
                                                key={`${action}-${index}`}
                                                className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2 text-sm"
                                            >
                                                {action}
                                            </li>
                                        ))}
                                    </ul>
                                </section>

                                <details className="rounded-2xl border border-border/70 bg-card p-4 lg:col-span-2">
                                    <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                                        <span className="flex items-center gap-2">
                                            <Brain className="h-4 w-4" />
                                            ML Metrics
                                        </span>
                                        <ChevronDown className="h-4 w-4" />
                                    </summary>
                                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                                        <div className="rounded-xl border border-border/60 bg-muted/30 p-3 text-sm">
                                            <div className="font-medium">Model weights</div>
                                            <ul className="mt-2 space-y-1">
                                                {Object.keys(modelWeights).length ? (
                                                    Object.entries(modelWeights).map(
                                                        ([name, value]) => (
                                                            <li key={name}>
                                                                {name}:{' '}
                                                                {formatNumber(
                                                                    Number(value) * 100,
                                                                    1,
                                                                )}
                                                                %
                                                            </li>
                                                        ),
                                                    )
                                                ) : (
                                                    <li>No model weight data available.</li>
                                                )}
                                            </ul>
                                        </div>
                                        <div className="rounded-xl border border-border/60 bg-muted/30 p-3 text-sm">
                                            <div className="font-medium">
                                                Prediction confidence intervals
                                            </div>
                                            <ul className="mt-2 space-y-1">
                                                {Object.keys(boundsMetadata).length ? (
                                                    Object.entries(boundsMetadata).map(
                                                        ([name, value]) => (
                                                            <li key={name}>
                                                                {name}:{' '}
                                                                {typeof value === 'number'
                                                                    ? formatNumber(value, 4)
                                                                    : String(value)}
                                                            </li>
                                                        ),
                                                    )
                                                ) : (
                                                    <li>
                                                        No confidence interval metadata available.
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                </details>

                                <details className="rounded-2xl border border-border/70 bg-card p-4 lg:col-span-2">
                                    <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                                        <span className="flex items-center gap-2">
                                            <Database className="h-4 w-4" />
                                            Raw Data
                                        </span>
                                        <ChevronDown className="h-4 w-4" />
                                    </summary>
                                    <div className="mt-4 space-y-3">
                                        <pre className="overflow-x-auto rounded-xl bg-muted/40 p-3 text-xs">
                                            {JSON.stringify(
                                                confirmationDetail.raw_debug || {},
                                                null,
                                                2,
                                            )}
                                        </pre>
                                        <pre className="overflow-x-auto rounded-xl bg-muted/40 p-3 text-xs">
                                            {JSON.stringify(
                                                {
                                                    marketNews,
                                                    forecast,
                                                },
                                                null,
                                                2,
                                            )}
                                        </pre>
                                    </div>
                                </details>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
